import React from 'react';
import { StudioSingle } from '../components/StudioSingle';
import { Helmet } from 'react-helmet';

/**
 * Helper to describe responsive images generated by sharp
 */
const responsiveImage = (basePath, name) => {
	const widths = [640, 960, 1280, 1920, 2560];

	return {
		jpg: {
			src: `${basePath}/${name}-1280.jpg`,
			srcSet: widths.map((w) => `${basePath}/${name}-${w}.jpg ${w}w`).join(', '),
		},
		webp: {
			srcSet: widths.map((w) => `${basePath}/${name}-${w}.webp ${w}w`).join(', '),
		},
	};
};

export const Studio = ({ transition }) => {
	// Generated image base paths (PUBLIC)
	const SLIDER_PATH = '/assets/img/generated/studio_slider';
	const GALLERY_PATH = '/assets/img/generated/studio_gallery';
	const FLOORPLAN_PATH = '/assets/img/generated/floorplan';
	const LOADINGBAY_PATH = '/assets/img/generated/loadingbay';
	const AREA_PATH = '/assets/img/generated/area';

	const slides = ['slide_1', 'slide_2', 'slide_3', 'slide_4', 'slide_5', 'slide_6', 'slide_7', 'slide_8', 'slide_9', 'slide_10', 'slide_11', 'slide_12', 'slide_13'].map((name) =>
		responsiveImage(SLIDER_PATH, name)
	);

	const gallery = ['gallery_1', 'gallery_2', 'gallery_3', 'gallery_4', 'gallery_5', 'gallery_6', 'gallery_7', 'gallery_8', 'gallery_9'].map((name) =>
		responsiveImage(GALLERY_PATH, name)
	);

	const floorplan = responsiveImage(FLOORPLAN_PATH, 'floorplan');
	const loadingbay = responsiveImage(LOADINGBAY_PATH, 'loadingbay');
	const area = responsiveImage(AREA_PATH, 'area_1');

	return (
		<>
			<Helmet>
				<title>Studio Rental in Stockholm – The Studio at Spring Studio</title>
				<meta
					name="description"
					content="The Studio at Spring Studio is a 640 m² studio rental in Stockholm, ideal for photography, film, commercials, music rehearsals, and creative productions. High ceilings, natural light, and easy access."
				/>
				<meta property="og:title" content="Studio Rental in Stockholm – The Studio at Spring Studio" />
				<meta
					property="og:description"
					content="The Studio at Spring Studio is a 640 m² studio rental in Stockholm, ideal for photography, film, commercials, music rehearsals, and creative productions."
				/>
				<link rel="canonical" href="https://springstudio.se/studio" />
			</Helmet>

			<StudioSingle
				transition={transition}
				title="The Studio"
				preamble="Spring Studio in Aspudden/Örnsberg offers 640 m² of versatile space for photo, film, music, and creative projects. With high ceilings, natural light, and full amenities, it’s perfect for large crews and flexible setups."
				studioNumber="[◎•]"
				slider={{
					title: 'Versitility',
					text: 'Spring Studio is designed to accommodate a wide range of productions. Whether you are planning a fashion or product photoshoot, shooting a commercial, recording a music session, or hosting a creative workshop, the open layout allows you to adapt the space to your needs. With flexible areas for lighting, sets, and crew, you can arrange the studio exactly how your project requires.',
					items: slides,
				}}
				gallery={{
					title: 'The Space',
					text: 'Inside the studio, you’ll find a bright and open environment ideal for both work and relaxation. Multiple seating areas allow for comfortable group discussions or breaks, while the large open floor gives you freedom to build sets or rehearse. The combination of natural light, open space, and flexible layout makes it an inspiring place to create.',
					items: gallery,
				}}
				floorplan={{
					title: 'Floorplan',
					text: 'The studio layout is straightforward and easy to work with. The open area provides plenty of room for sets and equipment, while the separate kitchen, toilet, and shower areas ensure comfort during long sessions. Elevator access and clear pathways from the loading bay make it simple to move crew and equipment in and out efficiently.',
					image: floorplan,
				}}
				loadingbay={{
					title: 'Access & Logistics',
					text: 'Built with professional productions in mind, Spring Studio features a connected loading bay and elevators for effortless access with large props or equipment. Commuting is easy thanks to the short walk from Örnsbergs metro station.',
					image: loadingbay,
				}}
				specification={{
					title: 'Specifications',
					text: 'Spring Studio offers reliable infrastructure for any production, with multiple 16A and 32A electrical connections and ample wall-mounted sockets. High-speed Wi-Fi covers the entire space, while large windows provide controlled natural light perfect for photography, film, or music rehearsals.',
				}}
				equipment={{
					title: 'Equipment',
					text: 'The studio comes equipped with all the essentials to make your production smooth and comfortable. Multiple tables and chairs accommodate large groups, the kitchen is stocked with basic appliances, and lounge areas offer a place to rest between sessions. The studio’s open space ensures that sets, lighting, or rehearsals can be set up without restrictions.',
				}}
				area={{
					title: 'Outside the studio',
					text: 'Spring Studio is located in a lively part of Aspudden/Örnsberg. Just around the corner you’ll find Örnsbergs Bageri, while the nearby Vinterviken area offers cafés, green spaces, and waterfront walks - perfect for breaks or creative pauses. Aspudden is also home to a variety of restaurants and take-away spots, and for those looking to wind down after a long day, the local pub Diset in Örnsberg is a great place to celebrate a wrap with a well-earned beer.',
					image: area,
				}}
				faq={{
					title: 'FAQ',
					text: 'If you have questions about the studio, we’ve got you covered.',
				}}
				contact={{
					title: 'Looks cool?',
					text: 'Spring Studio is ready for your next creative project. Reach out today to book your session and make the most of our versatile space.',
					ctaTitle: 'Book studio',
				}}
			/>
		</>
	);
};
